import { useRef, useState, type FormEvent } from 'react';
import type { App, Environment } from '@hangar/shared';
import { toast } from 'sonner';
import { useEnvVarMutations } from '@/api/hooks';
import type { EnvVar } from '@/api/schemas';
import { serverErrorsOf } from '@/lib/formErrors';
import { showErrorToast } from '@/lib/toasts';
import { isExpoPublic } from './envHelpers';
import { buildCreateBody, buildPatchBody, createdDescription, initialEnvVarForm, normalizeKey, validateEnvVarForm, warningToastOf, type EnvVarField, type EnvVarFormValues } from './envVarModel';
import type { ScopeSelection } from './ScopePicker';

export interface EnvVarFormProps {
  /** Present → edit metadata (value changes go through "New version"). */
  editing?: EnvVar;
  projectId: string;
  scope: ScopeSelection;
  apps: App[] | undefined;
  environments: Environment[];
  onDone: () => void;
}

/** Create (with an idempotency key that is renewed after each success) or patch the variable, and toast the outcome. */
function useSaveEnvVar({ editing, projectId, apps }: EnvVarFormProps, values: EnvVarFormValues) {
  const { create, patch } = useEnvVarMutations();
  const idempotencyKey = useRef(crypto.randomUUID());
  const save = async () => {
    if (editing) {
      await patch.mutateAsync({ id: editing.id, body: buildPatchBody(values), etag: editing.etag });
      toast.success(`${editing.key} updated`);
      return;
    }
    const result = await create.mutateAsync({ body: buildCreateBody(values, projectId, apps), idempotencyKey: idempotencyKey.current });
    idempotencyKey.current = crypto.randomUUID();
    toast.success(`${result.envVar.key} created`, { description: createdDescription(result) });
    const warning = warningToastOf(result);
    if (warning) toast.warning(warning.title, { description: warning.description });
  };
  return { save, pending: create.isPending || patch.isPending };
}

/** State, validation and submit of the variable form. The sections render what this returns. */
export function useEnvVarForm(props: EnvVarFormProps) {
  const { editing, scope, apps, onDone } = props;
  const [values, setValues] = useState<EnvVarFormValues>(() => initialEnvVarForm(editing, scope, apps));
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);
  const [server, setServer] = useState<Record<string, string>>({});
  const { save, pending } = useSaveEnvVar(props, values);
  const { errors, invalid } = validateEnvVarForm(values, editing);
  const patch = (p: Partial<EnvVarFormValues>) => setValues((v) => ({ ...v, ...p }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setProblem(null);
    setServer({});
    if (invalid) return;
    try {
      await save();
      onDone();
    } catch (err) {
      const mapped = serverErrorsOf(err, 'key');
      setServer(mapped.fields);
      if (Object.keys(mapped.fields).length === 0) setProblem(err);
      if (mapped.toast) showErrorToast(mapped.toast);
    }
  };
  /** The message to show under a field: the server's, else the local one once submitted (or `live` while typing). */
  const errorFor = (k: EnvVarField, live = false) => server[k] || (submitted || live ? errors[k] : undefined);
  return { values, patch, setKey: (key: string) => patch({ key: normalizeKey(key) }), expo: isExpoPublic(values.key), edit: Boolean(editing), editing, errorFor, server, submitted, problem, pending, onSubmit };
}

export type EnvVarFormApi = ReturnType<typeof useEnvVarForm>;
